// src/components/ShopModal.js
import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { getShopItems, purchaseShopItem, spinShopRoulette, getRawHistoryForStats } from '../services/api';
import CaseReward from './CaseReward';
import pointsIcon from '../assets/points_icon.png';
import StatsOverlay from './StatsOverlay';
import Modal from './Modal';
import { calculateUserStats } from '../utils/statsCalculator';

const SHOP_REWARD_PRIZES = [
  { type: 'points', value: 25 },
  { type: 'xp_booster', value: 200 },
  { type: 'points', value: 50 },
  { type: 'role', value: 2 },
  { type: 'xp_booster', value: 300 },
  { type: 'points', value: 75 },
  { type: 'jackpot', value: 'ДЖЕКПОТ' },
  { type: 'points', value: 100 },
  { type: 'role', value: 3 },
  { type: 'xp_booster', value: 500 },
  { type: 'points', value: 200 },
  { type: 'role', value: 5 },
];

export default function ShopModal({ profile, onPurchaseSuccess, activeSeason }) {
  const [shopData, setShopData] = useState({ categories: [] });
  const [loading, setLoading] = useState(true);
  const [purchaseStatus, setPurchaseStatus] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Окно подробностей товара (с long_description)
  const [detailedItem, setDetailedItem] = useState(null);

  // Рулетка
  const [isSpinning, setIsSpinning] = useState(false);
  const [spinResult, setSpinResult] = useState(null);

  // Статистика
  const [showStats, setShowStats] = useState(false);
  const [rawHistory, setRawHistory] = useState([]);
  const [timeframe, setTimeframe] = useState('alltime');

  const openStats = async () => {
    try {
      const res = await getRawHistoryForStats();
      setRawHistory(res.data);
      setShowStats(true);
    } catch (e) {
      console.error(e);
    }
  };

  const stats = useMemo(() => calculateUserStats(rawHistory, activeSeason, timeframe), [rawHistory, activeSeason, timeframe]);

  useEffect(() => {
    getShopItems()
      .then(res => setShopData(res.data || { categories: [] }))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleSpin = async () => {
    if (profile.totalPoints < 150) {
      alert('Недостаточно очков для прокрутки.');
      return;
    }
    setIsSpinning(true);
    setSpinResult(null);
    try {
      const res = await spinShopRoulette();
      setSpinResult(res.data.reward);
    } catch (error) {
      alert(error.response?.data?.error || 'Ошибка');
      setIsSpinning(false);
    }
  };

  const handleAnimationEnd = () => {
    setIsSpinning(false);
    setSpinResult(null);
    onPurchaseSuccess();
  };

  const handlePurchase = async (item) => {
    if (profile.totalPoints < item.cost) {
      alert('Недостаточно очков для покупки.');
      return;
    }
    if (!window.confirm(`Вы уверены, что хотите купить "${item.name}" за ${item.cost} очков?`)) {
      return;
    }

    setPurchaseStatus(prev => ({ ...prev, [item.id]: 'loading' }));
    try {
      await purchaseShopItem(item.id);
      setPurchaseStatus(prev => ({ ...prev, [item.id]: 'success' }));
      onPurchaseSuccess();
      setTimeout(() => {
        setPurchaseStatus(prev => ({ ...prev, [item.id]: null }));
        if (detailedItem?.id === item.id) setDetailedItem(null);
      }, 1500);
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Ошибка при покупке';
      setPurchaseStatus(prev => ({ ...prev, [item.id]: 'error', message: errorMsg }));
      setTimeout(() => {
        setPurchaseStatus(prev => ({ ...prev, [item.id]: null }));
      }, 3000);
    }
  };

  // Список категорий для навигации
  const categoryNames = useMemo(() => {
    return ['all', ...shopData.categories.map(c => c.name)];
  }, [shopData]);

  // Фильтрация товаров по выбранной вкладке
  const displayedCategories = useMemo(() => {
    if (selectedCategory === 'all') return shopData.categories;
    return shopData.categories.filter(c => c.name === selectedCategory);
  }, [shopData, selectedCategory]);

  return (
    <div className="shop-modern-container">
      {/* 1. Верхняя панель: Название магазина и баланс */}
      <div className="shop-top-navbar">
        <div className="shop-branding" onClick={openStats} title="Открыть статистику лавки">
          <span className="shop-sub-label">МАГАЗИН</span>
          <h1 className="shop-title">ᚳᚣᛒᛕᚣ ᚷᚣᚶᚣᚢᚣ</h1>
        </div>

        <div className="shop-user-badge">
          <div className="badge-text-group">
            <span className="badge-caption">Ваш баланс</span>
            <div className="badge-balance">
              {profile.totalPoints.toLocaleString()}
              <img src={pointsIcon} alt="pts" className="points-icon" />
            </div>
          </div>
        </div>
      </div>

      {/* 2. Hero-секция: Колесо удачи / Рулетка */}
      {/* 2. Hero-секция: Колесо удачи / Рулетка */}
      {activeSeason && (
        <div className="shop-roulette-hero">
          <div className="roulette-hero-glow"></div>
          {isSpinning && spinResult ? (
            <div className="roulette-active-stage">
              <CaseReward
                reward={spinResult}
                onAnimationEnd={handleAnimationEnd}
                ALL_PRIZES={SHOP_REWARD_PRIZES}
              />
            </div>
          ) : (
            <div className="roulette-hero-content">
              <div className="roulette-info">
                <span className="roulette-tag">РУЛЕТКА</span>
                <h2 className="roulette-title">Случайная награда</h2>
                <p className="roulette-desc">
                  Испытайте свою удачу! Множители опыта, эксклюзивные роли и редкий <strong>Джекпот</strong>.
                </p>
              </div>

              <button
                className="spin-cyber-btn"
                onClick={handleSpin}
                disabled={isSpinning || profile.totalPoints < 150}
              >
                <span>Крутить за 150</span>
                <img src={pointsIcon} alt="pts" className="points-icon" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* 3. Фильтры категорий */}
      <div className="shop-categories-nav">
        {categoryNames.map(cat => (
          <button
            key={cat}
            className={`cat-tab-btn ${selectedCategory === cat ? 'active' : ''}`}
            onClick={() => setSelectedCategory(cat)}
          >
            {cat === 'all' ? 'Все товары' : cat}
          </button>
        ))}
      </div>

      {/* 4. Сетка товаров */}
      {loading ? (
        <div className="shop-loading-state">
          <div className="shop-spinner"></div>
          <span>Загрузка витрины...</span>
        </div>
      ) : (
        <div className="shop-sections-wrap">
          {displayedCategories.map(category => (
            <div key={category.name} className="shop-cat-block">
              {selectedCategory === 'all' && (
                <div className="category-block-header">
                  <span className="cat-accent-bar"></span>
                  <h3>{category.name}</h3>
                </div>
              )}

              <div className="modern-items-grid">
                {category.items.map(item => {
                  const status = purchaseStatus[item.id];
                  const canAfford = profile.totalPoints >= item.cost;

                  return (
                    <div
                      key={item.id}
                      className={`modern-shop-card ${!canAfford ? 'unaffordable' : ''}`}
                      onClick={() => setDetailedItem(item)}
                    >
                      {/* Картинка с бейджем стоимости */}
                      <div className="card-media-wrapper">
                        {item.image ? (
                          <img src={item.image} alt={item.name} className="card-item-img" />
                        ) : (
                          <div className="card-img-placeholder">📦</div>
                        )}
                        <span className="card-cost-badge">
                          {item.cost} <img src={pointsIcon} alt="" className="points-icon-small" />
                        </span>
                      </div>

                      {/* Текстовая информация */}
                      <div className="card-text-body">
                        <h4 className="card-item-name">{item.name}</h4>
                        <p className="card-item-short-desc">{item.description}</p>
                      </div>

                      {/* Подвал карточки */}
                      <div className="card-actions-row" onClick={e => e.stopPropagation()}>
                        <button
                          type="button"
                          className="btn-card-details"
                          onClick={() => setDetailedItem(item)}
                        >
                          Подробнее
                        </button>

                        <button
                          type="button"
                          className={`btn-card-buy ${status === 'success' ? 'success' : ''} ${!canAfford ? 'disabled' : ''}`}
                          onClick={() => handlePurchase(item)}
                          disabled={!canAfford || status === 'loading'}
                        >
                          {status === 'loading' ? '...' : (status === 'success' ? '✓' : 'Купить')}
                        </button>
                      </div>

                      {status === 'error' && (
                        <div className="card-error-notice">{purchaseStatus.message}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 5. Модальное окно полного описания (long_description) через Portal */}
      {detailedItem && createPortal(
        <div className="modal-overlay" onClick={() => setDetailedItem(null)}>
          <div className="modal-content modal-size-wide shop-detail-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close-button" onClick={() => setDetailedItem(null)}>✕</button>

            <div className="detail-modal-layout">
              {/* Левая колонка: Крупный арт */}
              <div className="detail-media-column">
                {detailedItem.image ? (
                  <img src={detailedItem.image} alt={detailedItem.name} className="detail-hero-img" />
                ) : (
                  <div className="detail-img-placeholder">📦</div>
                )}
                <div className="detail-cost-box">
                  <span className="cost-caption">Стоимость товара</span>
                  <div className="cost-value">
                    {detailedItem.cost.toLocaleString()}
                    <img src={pointsIcon} alt="pts" className="points-icon" />
                  </div>
                </div>
              </div>

              {/* Правая колонка: Вся информация и long_description */}
              <div className="detail-info-column">
                <span className="detail-tag">ТОВАР ИЗ ЛАВКИ</span>
                <h2 className="detail-title">{detailedItem.name}</h2>
                <p className="detail-lead">{detailedItem.description}</p>

                {/* Полное расширенное описание */}
                <div className="detail-long-desc-box">
                  <span className="desc-box-title">ПОЛНОЕ ОПИСАНИЕ И СВОЙСТВА</span>
                  <p className="desc-box-content">
                    {detailedItem.long_description || 'У этого предмета нет дополнительного описания.'}
                  </p>
                </div>

                {/* Калькулятор баланса */}
                <div className="detail-balance-summary">
                  <div className="balance-calc-row">
                    <span>Ваш баланс:</span>
                    <span>{profile.totalPoints} pts</span>
                  </div>
                  <div className="balance-calc-row">
                    <span>Останется после покупки:</span>
                    <span className={profile.totalPoints >= detailedItem.cost ? 'pts-positive' : 'pts-negative'}>
                      {profile.totalPoints - detailedItem.cost} pts
                    </span>
                  </div>
                </div>

                {/* Кнопка мгновенной покупки */}
                <div className="detail-modal-footer">
                  <button
                    type="button"
                    className="detail-buy-button"
                    onClick={() => handlePurchase(detailedItem)}
                    disabled={profile.totalPoints < detailedItem.cost || purchaseStatus[detailedItem.id] === 'loading'}
                  >
                    {purchaseStatus[detailedItem.id] === 'loading' ? (
                      'Обработка...'
                    ) : purchaseStatus[detailedItem.id] === 'success' ? (
                      '✓ Куплено!'
                    ) : profile.totalPoints >= detailedItem.cost ? (
                      `Купить за ${detailedItem.cost} pts`
                    ) : (
                      'Недостаточно очков'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Окно статистики лавки */}
      {showStats && (
        <Modal isOpen={showStats} onClose={() => setShowStats(false)} size="narrow">
          <StatsOverlay
            title="Статистика Лавки"
            stats={stats}
            type="shop"
            timeframe={timeframe}
            onTimeframeChange={setTimeframe}
          />
        </Modal>
      )}
    </div>
  );
}